import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  CheckCircle,
  Users,
  LayoutGrid,
  ClipboardList,
  Brain,
  ChevronDown,
} from 'lucide-react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

// ---------------------------------------------------------------------------
// EarlyAdopterMockup
// ---------------------------------------------------------------------------

function StatCounter({
  targetRef,
  value,
  label,
  icon,
}: {
  targetRef: React.RefObject<HTMLSpanElement>
  value: number
  label: string
  icon: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex items-center gap-1.5 text-[#359d9e]">{icon}</div>
      <span
        ref={targetRef}
        className="text-2xl font-bold text-[#000f0f] tabular-nums"
      >
        {value}
      </span>
      <span className="text-[11px] text-[#6e7878] leading-tight text-center">
        {label}
      </span>
    </div>
  )
}

const recentActivity = [
  { name: 'Budi Santoso', action: 'completed Leadership Assessment', time: '2m ago' },
  { name: 'Sari Dewi', action: 'joined Wawasan module', time: '14m ago' },
  { name: 'Andi Pratama', action: 'CV screened — 84% match', time: '31m ago' },
]

function EarlyAdopterMockup({
  empRef,
  modRef,
  assRef,
}: {
  empRef: React.RefObject<HTMLSpanElement>
  modRef: React.RefObject<HTMLSpanElement>
  assRef: React.RefObject<HTMLSpanElement>
}) {
  return (
    <div className="w-full max-w-[420px] rounded-2xl bg-white shadow-[0_8px_40px_rgba(53,157,158,0.22)] border border-[#d5f2ef] overflow-hidden select-none">
      {/* Card header */}
      <div className="bg-gradient-to-r from-[#359d9e] to-[#27777a] px-5 py-4 flex items-center justify-between">
        <div>
          <p className="text-[11px] font-medium text-white/70 uppercase tracking-widest">
            Company Onboarding
          </p>
          <p className="text-base font-semibold text-white mt-0.5">
            PT Andalan Karya
          </p>
        </div>
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
          <span className="text-white font-bold text-sm">AK</span>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 divide-x divide-[#e6e7e7] px-4 py-4">
        <StatCounter
          targetRef={empRef}
          value={47}
          label="Employees"
          icon={<Users className="w-4 h-4" />}
        />
        <StatCounter
          targetRef={modRef}
          value={3}
          label="Modules Active"
          icon={<LayoutGrid className="w-4 h-4" />}
        />
        <StatCounter
          targetRef={assRef}
          value={12}
          label="Assessments"
          icon={<ClipboardList className="w-4 h-4" />}
        />
      </div>

      {/* Activity feed */}
      <div className="px-5 pb-4">
        <p className="text-[11px] font-semibold text-[#b3b7b7] uppercase tracking-widest mb-3">
          Recent Activity
        </p>
        <div className="flex flex-col gap-2">
          {recentActivity.map((item) => (
            <div key={item.name} className="flex items-start gap-2.5">
              <div className="w-7 h-7 rounded-full bg-[#f2fbfa] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[10px] font-bold text-[#359d9e]">
                  {item.name.charAt(0)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#000f0f] leading-snug">
                  <span className="font-medium">{item.name}</span>{' '}
                  {item.action}
                </p>
                <p className="text-[10px] text-[#b3b7b7] mt-0.5">{item.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI insight pill */}
      <div className="mx-5 mb-5 flex items-center gap-2 bg-[#f2fbfa] border border-[#b3e8e8] rounded-xl px-4 py-3">
        <Brain className="w-4 h-4 text-[#359d9e] flex-shrink-0" />
        <p className="text-xs text-[#1a2727] leading-snug">
          <span className="font-semibold text-[#359d9e]">AI Insight:</span>{' '}
          3 employees show leadership potential — recommend 9-Box review
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Demo form schema
// ---------------------------------------------------------------------------

const demoSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Enter a valid work email'),
  company: z.string().min(1, 'Company name is required'),
  size: z.enum(['1-15', '16-50', '51-250', '250+'], {
    errorMap: () => ({ message: 'Select a company size' }),
  }),
  phone: z.string().optional(),
  message: z.string().optional(),
  website: z.string().optional(), // honeypot
})

type DemoFormValues = z.infer<typeof demoSchema>

function DemoForm() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DemoFormValues>({
    resolver: zodResolver(demoSchema),
    defaultValues: { size: undefined },
  })

  const onSubmit = (data: DemoFormValues) => {
    // Honeypot check — silently abort if bot filled the hidden field
    if (data.website) return

    const subject = encodeURIComponent(
      `Demo Request — ${data.name} from ${data.company}`,
    )
    const body = encodeURIComponent(
      [
        `Name: ${data.name}`,
        `Email: ${data.email}`,
        `Company: ${data.company}`,
        `Company Size: ${data.size}`,
        data.phone ? `Phone: ${data.phone}` : null,
        data.message ? `\nMessage:\n${data.message}` : null,
      ]
        .filter(Boolean)
        .join('\n'),
    )
    window.location.href = `mailto:support@pelajari.ai?subject=${subject}&body=${body}`
  }

  const inputCls =
    'w-full rounded-lg border border-[#d5f2ef] bg-white px-3.5 py-2.5 text-sm text-[#000f0f] placeholder:text-[#b3b7b7] focus:outline-none focus:ring-2 focus:ring-[#359d9e]/40 focus:border-[#359d9e] transition'
  const errCls = 'mt-1 text-xs text-red-400'

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 mt-6" noValidate>
      {/* Honeypot — hidden from real users */}
      <input
        {...register('website')}
        type="text"
        name="website"
        tabIndex={-1}
        aria-hidden="true"
        style={{ display: 'none' }}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <input {...register('name')} placeholder="Your Name *" className={inputCls} />
          {errors.name && <p className={errCls}>{errors.name.message}</p>}
        </div>
        <div>
          <input {...register('email')} type="email" placeholder="Work Email *" className={inputCls} />
          {errors.email && <p className={errCls}>{errors.email.message}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <input {...register('company')} placeholder="Company Name *" className={inputCls} />
          {errors.company && <p className={errCls}>{errors.company.message}</p>}
        </div>
        <div>
          <div className="relative">
            <select
              {...register('size')}
              defaultValue=""
              className={`${inputCls} appearance-none pr-8 cursor-pointer`}
            >
              <option value="" disabled>Company Size *</option>
              <option value="1-15">1 – 15 employees</option>
              <option value="16-50">16 – 50 employees</option>
              <option value="51-250">51 – 250 employees</option>
              <option value="250+">250+ employees</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#b3b7b7]" />
          </div>
          {errors.size && <p className={errCls}>{errors.size.message}</p>}
        </div>
      </div>

      <div>
        <input {...register('phone')} placeholder="Phone (optional)" className={inputCls} />
      </div>

      <div>
        <textarea
          {...register('message')}
          placeholder="Any specific goals or questions? (optional)"
          rows={3}
          className={`${inputCls} resize-none`}
        />
      </div>

      <button
        type="submit"
        className="w-full sm:w-auto self-start bg-[#359d9e] text-white hover:bg-[#27777a] px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors"
      >
        Send Demo Request
      </button>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Benefits
// ---------------------------------------------------------------------------

const benefits = [
  'Run live psychometric assessments, recruitment pipelines, and talent reviews on your real team',
  'Receive dedicated support and AI feature customization',
  'Help shape our product roadmap with your direct feedback',
  'Enjoy preferential pricing for your early partnership',
]

// ---------------------------------------------------------------------------
// LandingCTA
// ---------------------------------------------------------------------------

export function LandingCTA() {
  const [formOpen, setFormOpen] = useState(false)

  const sectionRef = useRef<HTMLElement>(null)
  const leftRef = useRef<HTMLDivElement>(null)
  const mockupRef = useRef<HTMLDivElement>(null)

  // Stat counter refs
  const empRef = useRef<HTMLSpanElement>(null)
  const modRef = useRef<HTMLSpanElement>(null)
  const assRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Left content fade-up
      gsap.from(leftRef.current, {
        y: 28,
        opacity: 0,
        duration: 0.7,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: leftRef.current,
          start: 'top 82%',
          once: true,
        },
      })

      // Mockup slide in from right
      gsap.from(mockupRef.current, {
        x: 40,
        opacity: 0,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: mockupRef.current,
          start: 'top 82%',
          once: true,
          onEnter: () => {
            // Count-up on the three stat numbers
            const targets = [
              { ref: empRef, end: 47 },
              { ref: modRef, end: 3 },
              { ref: assRef, end: 12 },
            ]
            targets.forEach(({ ref, end }, i) => {
              if (!ref.current) return
              gsap.fromTo(
                { val: 0 },
                { val: end },
                {
                  val: end,
                  duration: 1.2,
                  delay: i * 0.15,
                  ease: 'power1.out',
                  roundProps: 'val',
                  onUpdate: function () {
                    if (ref.current) ref.current.textContent = String(Math.round(this.targets()[0].val))
                  },
                },
              )
            })
          },
        },
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      id="book-demo"
      ref={sectionRef}
      className="relative bg-[#359d9e] py-16 overflow-hidden"
    >
      {/* Decorative background wave */}
      <div className="absolute right-0 top-0 bottom-0 w-[554px] opacity-10 hidden md:block pointer-events-none">
        <svg viewBox="0 0 554 600" fill="none" className="w-full h-full">
          <path d="M554 0C354 100 254 250 354 400C454 550 554 600 554 600V0Z" fill="white" fillOpacity="0.3" />
        </svg>
      </div>

      <div className="relative z-10 flex flex-col items-center gap-12 px-6 md:px-10 lg:flex-row lg:items-start lg:justify-between lg:px-[120px] max-w-[1440px] mx-auto">
        {/* Left — benefits + form trigger */}
        <div ref={leftRef} className="max-w-[560px] w-full text-center lg:text-left">
          <h2 className="text-[30px] font-semibold leading-[38px] tracking-[-0.5px] text-white mb-4 sm:text-[34px] sm:leading-[42px] lg:text-[36px] lg:leading-[45px] lg:tracking-[-0.75px]">
            Partner With Us: Become an Early Adopter
          </h2>
          <p className="text-base text-white/90 mb-8">
            We are currently opening a limited-opportunity Pilot Implementation Program for
            innovative companies. Partner with us to shape the future of HR tech in Indonesia.
          </p>

          <p className="text-base font-semibold text-white mb-4">
            As an early partner, you will:
          </p>

          <div className="flex flex-col gap-3 mb-8">
            {benefits.map((benefit) => (
              <div
                key={benefit}
                className="flex items-start gap-3 justify-center lg:justify-start"
              >
                <CheckCircle className="w-6 h-6 text-white flex-shrink-0 mt-0.5" />
                <span className="text-base text-white">{benefit}</span>
              </div>
            ))}
          </div>

          {/* Book a Demo toggle button */}
          <button
            type="button"
            onClick={() => setFormOpen((v) => !v)}
            className="inline-flex items-center gap-2 bg-white text-[#359d9e] hover:bg-white/90 border border-white px-6 py-2.5 font-semibold rounded-lg transition-colors text-sm"
          >
            Book A Demo
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-300 ${formOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Expandable form */}
          <div
            style={{
              maxHeight: formOpen ? '700px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.4s ease',
            }}
          >
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-5 py-5 mt-5">
              <DemoForm />
            </div>
          </div>
        </div>

        {/* Right — EarlyAdopterMockup */}
        <div ref={mockupRef} className="flex justify-center lg:justify-end w-full lg:w-auto lg:pt-4">
          <EarlyAdopterMockup empRef={empRef} modRef={modRef} assRef={assRef} />
        </div>
      </div>
    </section>
  )
}
